import React from 'react';
import Panel from '../../components/ui/Panel';
import { Link } from 'react-router-dom';

const NavigationBreadcrumbs: React.FC = () => {
  return (
    <div className="space-y-8">
      <h1 className="text-3xl text-osrs-gold font-bold uppercase mb-4">Navigation Breadcrumbs</h1>
      
      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Overview</h2>
        <Panel>
          <p className="text-osrs-yellow">
            Breadcrumbs provide a way to track and navigate through hierarchical structures in the OSRS interface.
            They help users understand their current location and navigate back through parent sections.
          </p>
        </Panel>
      </section>

      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Basic Example</h2>
        <Panel>
          <div className="space-y-4">
            <nav aria-label="Breadcrumb" className="flex items-center space-x-2">
              <Link to="/" className="text-osrs-yellow hover:text-osrs-gold">
                Home
              </Link>
              <span className="text-osrs-yellow">&gt;</span>
              <Link to="/patterns" className="text-osrs-yellow hover:text-osrs-gold">
                Patterns
              </Link>
              <span className="text-osrs-yellow">&gt;</span>
              <span className="text-osrs-gold" aria-current="page">
                Breadcrumbs
              </span>
            </nav>
          </div>
        </Panel>
      </section>

      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Implementation</h2>
        <Panel>
          <pre className="bg-osrs-black p-4 rounded font-mono text-sm text-osrs-yellow overflow-x-auto">
{`interface BreadcrumbProps {
  items: {
    label: string;
    href?: string;
  }[];
}

const Breadcrumbs: React.FC<BreadcrumbProps> = ({ items }) => (
  <nav aria-label="Breadcrumb" className="flex items-center space-x-2">
    {items.map((item, index) => {
      const isLast = index === items.length - 1;
      
      return (
        <React.Fragment key={item.label}>
          {isLast ? (
            <span 
              className="text-osrs-gold"
              aria-current="page"
            >
              {item.label}
            </span>
          ) : (
            <>
              <Link
                to={item.href || '#'}
                className="text-osrs-yellow hover:text-osrs-gold"
              >
                {item.label}
              </Link>
              <span className="text-osrs-yellow">&gt;</span>
            </>
          )}
        </React.Fragment>
      );
    })}
  </nav>
);

// Usage
const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Patterns', href: '/patterns' },
  { label: 'Breadcrumbs' }
];

<Breadcrumbs items={breadcrumbItems} />`}
          </pre>
        </Panel>
      </section>

      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Accessibility</h2>
        <Panel>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2">
            <li>Use nav element with aria-label="Breadcrumb"</li>
            <li>Mark current page with aria-current="page"</li>
            <li>Use semantic separators between items</li>
            <li>Ensure keyboard navigation support</li>
            <li>Maintain sufficient color contrast</li>
          </ul>
        </Panel>
      </section>

      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Best Practices</h2>
        <Panel>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2">
            <li>Keep breadcrumb labels concise</li>
            <li>Use clear visual separators</li>
            <li>Highlight the current page</li>
            <li>Maintain consistent styling</li>
            <li>Consider truncation for long paths</li>
            <li>Ensure mobile responsiveness</li>
          </ul>
        </Panel>
      </section>

      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Variants</h2>
        <Panel>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl text-osrs-gold font-bold mb-3">With Icons</h3>
              <nav aria-label="Breadcrumb with icons" className="flex items-center space-x-2">
                <span className="text-osrs-yellow">🏠</span>
                <Link to="/" className="text-osrs-yellow hover:text-osrs-gold">
                  Home
                </Link>
                <span className="text-osrs-yellow">&gt;</span>
                <span className="text-osrs-yellow">📑</span>
                <Link to="/patterns" className="text-osrs-yellow hover:text-osrs-gold">
                  Patterns
                </Link>
                <span className="text-osrs-yellow">&gt;</span>
                <span className="text-osrs-yellow">🔗</span>
                <span className="text-osrs-gold" aria-current="page">
                  Breadcrumbs
                </span>
              </nav>
            </div>

            <div>
              <h3 className="text-xl text-osrs-gold font-bold mb-3">Compact</h3>
              <nav aria-label="Compact breadcrumb" className="flex items-center space-x-1 text-sm">
                <Link to="/" className="text-osrs-yellow hover:text-osrs-gold">
                  Home
                </Link>
                <span className="text-osrs-yellow">/</span>
                <Link to="/patterns" className="text-osrs-yellow hover:text-osrs-gold">
                  Patterns
                </Link>
                <span className="text-osrs-yellow">/</span>
                <span className="text-osrs-gold" aria-current="page">
                  Breadcrumbs
                </span>
              </nav>
            </div>
          </div>
        </Panel>
      </section>
    </div>
  );
};

export default NavigationBreadcrumbs; 